import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import EntitySelect from "./EntitySelect";
import AtecoInput from "./AtecoInput";
import CommittenteSelect from "./CommittenteSelect";
import LookupSelect from "./LookupSelect";
import ReminderPicker from "./ReminderPicker";
import api, { formatApiError } from "../lib/api";
import { RELATION_KEYS } from "../lib/entities";
import { toast } from "sonner";

const NONE = "__none__";

function toDateInput(iso, withTime) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return withTime ? `${base}T${pad(d.getHours())}:${pad(d.getMinutes())}` : base;
}

export default function FormDialog({ open, onOpenChange, title, fields, initial, endpoint, method = "post", onSaved, extra }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(initial ? { ...initial } : {});
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const renderField = (f) => {
    const val = form[f.name];
    switch (f.type) {
      case "textarea":
        return <Textarea data-testid={`field-${f.name}`} value={val || ""} onChange={(e) => set(f.name, e.target.value)} rows={3} />;
      case "number":
        return <Input data-testid={`field-${f.name}`} type="number" value={val ?? ""} onChange={(e) => set(f.name, e.target.value === "" ? null : Number(e.target.value))} />;
      case "switch":
        return <div className="pt-1"><Switch data-testid={`field-${f.name}`} checked={!!val} onCheckedChange={(v) => set(f.name, v)} /></div>;
      case "tags":
        return <Input data-testid={`field-${f.name}`} value={Array.isArray(val) ? val.join(", ") : (val || "")} onChange={(e) => set(f.name, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="tag1, tag2" />;
      case "date":
        return <Input data-testid={`field-${f.name}`} type="date" value={toDateInput(val, false)} onChange={(e) => set(f.name, e.target.value ? new Date(e.target.value).toISOString() : null)} />;
      case "datetime":
        return <Input data-testid={`field-${f.name}`} type="datetime-local" value={toDateInput(val, true)} onChange={(e) => set(f.name, e.target.value ? new Date(e.target.value).toISOString() : null)} />;
      case "company":
        return <EntitySelect kind="company" value={val} onChange={(v) => set(f.name, v)} />;
      case "contact":
        return <EntitySelect kind="contact" value={val} onChange={(v) => set(f.name, v)} companyId={form.company_id} />;
      case "opportunity":
      case "lead":
        return <EntitySelect kind={f.type} value={val} onChange={(v) => set(f.name, v)} companyId={form.company_id} />;
      case "pipeline":
        return <EntitySelect kind="pipeline" value={val} onChange={(v) => setForm((p) => ({ ...p, pipeline_id: v, stage_id: null }))} />;
      case "user":
        return <EntitySelect kind="user" value={val} onChange={(v) => set(f.name, v)} />;
      case "project":
        return <EntitySelect kind="project" value={val} onChange={(v) => set(f.name, v)} />;
      case "stage":
        return <EntitySelect kind="stage" value={val} onChange={(v) => set(f.name, v)} pipelineId={form.pipeline_id} />;
      case "select":
        return (
          <Select value={val || NONE} onValueChange={(v) => set(f.name, v === NONE ? null : v)}>
            <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
            <SelectContent>
              {!f.required && <SelectItem value={NONE}>—</SelectItem>}
              {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        );
      case "lookup":
        return <LookupSelect lookup={f.lookup} testid={`field-${f.name}`} value={val || ""} onChange={(v) => set(f.name, v || null)} required={f.required} />;
      case "ateco":
        return <AtecoInput testid={`field-${f.name}`} value={val || ""} onChange={(v) => set(f.name, v)} />;
      case "committente":
        return <CommittenteSelect testid={`field-${f.name}`} value={val || ""} onChange={(v) => set(f.name, v)} />;
      case "reminders":
        return <ReminderPicker testid={`field-${f.name}`} value={val} onChange={(v) => set(f.name, v)} />;
      default:
        return <Input data-testid={`field-${f.name}`} value={val || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} />;
    }
  };

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name]) {
        toast.error(`Campo obbligatorio: ${f.label}`);
        return;
      }
    }
    setSaving(true);
    try {
      // whitelist: only declared fields + relation keys + extra → never persist derived/enriched data
      const allowed = new Set([...fields.map((f) => f.name), ...RELATION_KEYS, ...Object.keys(extra || {})]);
      const payload = Object.fromEntries(Object.entries({ ...form, ...(extra || {}) }).filter(([k]) => allowed.has(k)));
      if ("recurrence_freq" in payload) {
        payload.recurrence = payload.recurrence_freq && payload.recurrence_freq !== "none" ? { freq: payload.recurrence_freq, interval: 1, until: payload.recurrence_until || null } : null;
        delete payload.recurrence_freq; delete payload.recurrence_until;
      }
      const { data } = await api[method](endpoint, payload);
      toast.success("Salvato con successo");
      onOpenChange(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="sr-only">Compila i campi e salva</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2 [&_input]:min-h-[44px] sm:[&_input]:min-h-0 [&_button[role=combobox]]:min-h-[44px] sm:[&_button[role=combobox]]:min-h-0">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">
                {f.label}{f.required && <span className="text-red-500"> *</span>}
              </Label>
              {renderField(f)}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-btn">Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="form-submit-btn" className="bg-navy-900 hover:bg-navy-700 text-white">
            {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
